import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import Login from "./pages/Login";
import Search from "./pages/Search";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import AppLayout from "./layout/AppLayout";
import SettingsPage from "./pages/Settings";
import AddressDetailWithLayout from "./pages/AddressDetailWithLayout";
import NoteMap from "./pages/NoteMap";
import ReportsHome from "./pages/ReportsHome";
import ReportsStatus from "./pages/ReportsStatus";
import ReportsAgent from "./pages/ReportsAgent";
import ReportsCoverageClusters from "./pages/ReportsCoverageClusters";
import ReportsCoverageHeatmap from "./pages/ReportsCoverageHeatmap";
import ReportsCoverage from "./pages/ReportsCoverage";
import ReportsCoverageDeck from "./pages/ReportsCoverageDeck";
import { useEffect, useState } from "react";
import { Button, Snackbar, IconButton } from "@mui/material";
import { useNoteSyncBootstrap } from "./offline/noteSyncStore";
import { useDownloadBootstrap } from "./offline/useDownloadBootstrap";
import ToastHost from "./ui/ToastHost";
import SyncManager from "./pages/SyncManager";
import LoadingSplash from "./components/LoadingSplash";
import OfflineManager from "./pages/OfflineManager";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocationPermissionDialog from "./location/LocationPermissionDialog";
import { useLocationBootstrap } from "./location/useLocationBootstrap";

function AutoRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSplash />;
  return user ? <Navigate to="/search" /> : <Navigate to="/login" />;
}

// function ScrollToTop() {
//   const { pathname } = useLocation();
//   useEffect(() => {
//     window.scrollTo({ top: 0, behavior: "instant" });
//   }, [pathname]);
//   return null;
// }

// maintain older url for a minute, but can delete this later
function RedirectAddress() {
  const { udprn } = useParams();
  return <Navigate to={`/search/address/${udprn}`} replace />;
}

function HeaderBack({ fallback }: { fallback: string }) {
  const navigate = useNavigate();
  return (
    <IconButton
      color="inherit"
      onClick={() => {
        if (window.history.length > 2) navigate(-1);
        else navigate(fallback);
      }}
      size="small"
    >
      <ArrowBackIosNewIcon fontSize="small" />
    </IconButton>
  );
}


export default function App() {
   const [updateAvailable, setUpdateAvailable] = useState(false);
 const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useNoteSyncBootstrap();
  useDownloadBootstrap();
  useLocationBootstrap();


  useEffect(() => {
    const handler = (e: any) => {
      setUpdateAvailable(true)
      setRegistration(e.detail); 
    }


  window.addEventListener('sw.update', handler);
  return () => window.removeEventListener('sw.update', handler);
  }, [])

  const refresh = () => {
    if (registration?.waiting) {
      // Tell the waiting SW to activate
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
    window.location.reload()
  }
  return (
    <AuthProvider>
      <BrowserRouter>
   
        <Routes>

          {/* root redirect */}
          <Route path="/" element={<AutoRedirect />} />

          {/* login */}
          <Route path="/login" element={<Login />} />

          {/* protected */}
          <Route path="/search"element={
              <ProtectedRoute><AppLayout><Search /></AppLayout></ProtectedRoute>
            }>
            {/* nested sheet */}
            <Route path="address/:udprn" element={<AddressDetailWithLayout />} />
          </Route>

          <Route path="/address/:udprn" element={<RedirectAddress />} />

          <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage/></AppLayout></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsHome /></AppLayout></ProtectedRoute>} />
          <Route path="/reports/maps" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><NoteMap /></AppLayout></ProtectedRoute>} />
          <Route path="/reports/coverage" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsCoverage /></AppLayout></ProtectedRoute>} />
          <Route path="/reports/coverage-clusters" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsCoverageClusters /></AppLayout></ProtectedRoute>} />
          <Route path="/reports/coverage-heatmap" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsCoverageHeatmap /></AppLayout></ProtectedRoute>} />
          <Route path="/reports/coverage-maplibre" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsCoverageDeck /></AppLayout></ProtectedRoute>} />
          <Route path="/reports/status" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsStatus /></AppLayout></ProtectedRoute>} />
          <Route path="/reports/agent" element={<ProtectedRoute><AppLayout headerAction={<HeaderBack fallback="/reports" />}><ReportsAgent /></AppLayout></ProtectedRoute>} />
          <Route path="/note-map" element={<Navigate to="/reports/maps" replace />} />
          <Route
            path="/settings/offline"
            element={
              <ProtectedRoute>
                <AppLayout headerAction={<HeaderBack fallback="/settings" />}>
                  <OfflineManager />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/sync"
            element={
              <ProtectedRoute>
                <AppLayout headerAction={<HeaderBack fallback="/search" />}>
                  <SyncManager />
                </AppLayout>
              </ProtectedRoute>
            }
          />

        </Routes>
      </BrowserRouter>
            <Snackbar
        open={updateAvailable}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="A new version is available"
        action={
          <Button color="secondary" size="small" onClick={refresh}>
            Refresh
          </Button>
        }
      />
      <ToastHost />
      <LocationPermissionDialog />
    </AuthProvider>
  );
}
